import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import type { TenantMe } from '@studio/contracts';
import { AccessGate } from '@/components/access-gate';
import { Shell } from '@/components/shell';
import { setActiveBrand, withoutStaleBrand } from '@/lib/active-brand';
import { supabase } from '@/lib/supabase';
import { accessDenied, isForbidden, isUnauthorised, queryClient, trpc } from '@/lib/trpc';
import { roleAtLeast } from '@studio/contracts';
import { AuthCallbackPage } from '@/routes/auth-callback';
import { ApprovePage } from '@/routes/approve';
import { BrowsePage } from '@/routes/browse';
import { ComposePage } from '@/routes/compose';
import { ComposeFastPage } from '@/routes/compose-fast';
import { ArchivePage } from '@/routes/archive';
import { PublishedPage } from '@/routes/published';
import { ScheduledPage } from '@/routes/scheduled';
import { CreatorUploadPage } from '@/routes/creator-upload';
import { HomePage } from '@/routes/home';
import { AnalysePage } from '@/routes/analyse';
import { UploadPage } from '@/routes/upload';
import { LibraryPage } from '@/routes/library';
import { SettingsPage } from '@/routes/settings';
import { SignInPage } from '@/routes/sign-in';
import { SignUpPage } from '@/routes/sign-up';
import { TaggingPage } from '@/routes/tagging';
import { UsageLogPage } from '@/routes/usage-log';
import { WelcomePage } from '@/routes/welcome';

interface RouterContext { me: TenantMe | null }

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });

/** The two doors bounce a signed-in browser home. */
const signedOut = async (): Promise<void> => {
  const { data } = await supabase().auth.getSession();
  if (data.session) throw redirect({ to: '/' });
};

const signInRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sign-in', component: SignInPage,
  validateSearch: (s: Record<string, unknown>): { email?: string; invite?: string } => ({
    ...(typeof s['email'] === 'string' && s['email'].includes('@') ? { email: s['email'] } : {}),
    ...(typeof s['invite'] === 'string' && s['invite'] ? { invite: s['invite'] } : {}),
  }),
  beforeLoad: signedOut });

const signUpRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sign-up', component: SignUpPage, beforeLoad: signedOut });

const callbackRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/callback', component: AuthCallbackPage });

/** tenant.me is never gated by role, so its one refusal is a stored brand this account cannot reach. */
const fetchMe = (): Promise<TenantMe> => withoutStaleBrand(() => queryClient.fetchQuery(trpc.tenant.me.queryOptions()), isForbidden);

/** Who is signed in, or the door. A signed-in account with no workspace is sent to found one. */
async function loadMe(): Promise<TenantMe> {
  const { data } = await supabase().auth.getSession();
  if (!data.session) throw redirect({ to: '/sign-in' });
  try {
    const me = await fetchMe();
    if (me.brands.length === 0) throw redirect({ to: '/welcome' });
    if (me.active) setActiveBrand(me.active.brandSlug);
    return me;
  } catch (err) {
    if (isUnauthorised(err)) {
      await supabase().auth.signOut();
      throw redirect({ to: '/sign-in' });
    }
    throw err;
  }
}

/** Founding a workspace: a session with no brands lands here; one with brands goes home. */
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
  beforeLoad: async () => {
    const { data } = await supabase().auth.getSession();
    if (!data.session) throw redirect({ to: '/sign-in' });
    const me = await fetchMe();
    if (me.brands.length > 0) throw redirect({ to: '/' });
    return { me };
  },
});

/** Everything behind sign-in: resolves the tenant once, hands it to every child, gates on the organisation's access. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: async () => {
    const me = await loadMe();
    if (me.active?.role === 'creator') throw redirect({ to: '/creator' });
    return { me };
  },
  component: AppLayout,
});

function AppLayout() {
  const { me } = appRoute.useRouteContext();
  if (!me) return null;
  if (me.active && !me.hasAccess) return <AccessGate me={me} />;
  return <Shell me={me} />;
}

/**
 * Creators live on one screen with no sidebar, so it sits beside the app layout, not inside it.
 * Studio roles can open it too: solo creators upload, remix and publish themselves.
 */
const creatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creator',
  component: CreatorUploadPage,
  beforeLoad: async () => ({ me: await loadMe() }),
  errorComponent: ({ error }) => <ErrorPanel error={error} />,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage,
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const uploadRoute = createRoute({ getParentRoute: () => appRoute, path: '/upload', component: UploadPage,
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const analyseRoute = createRoute({ getParentRoute: () => appRoute, path: '/analyse', component: AnalysePage,
  validateSearch: (s: Record<string, unknown>): { asset?: string; snippet?: string } => ({
    ...(typeof s['asset'] === 'string' ? { asset: s['asset'] } : {}),
    ...(typeof s['snippet'] === 'string' ? { snippet: s['snippet'] } : {}),
  }),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const browseRoute = createRoute({ getParentRoute: () => appRoute, path: '/browse', component: BrowsePage,
  validateSearch: (s: Record<string, unknown>): { tab?: 'video' | 'photos' | 'audio'; pickSlot?: number; pick?: 'analyse' | 'tagging'; pack?: string } => ({
    ...(s['tab'] === 'video' || s['tab'] === 'photos' || s['tab'] === 'audio' ? { tab: s['tab'] as 'video' | 'photos' | 'audio' } : {}),
    ...(typeof s['pack'] === 'string' && /^[0-9a-f-]{36}$/.test(s['pack']) ? { pack: s['pack'] } : {}),
    ...(s['pick'] === 'analyse' || s['pick'] === 'tagging' ? { pick: s['pick'] as 'analyse' | 'tagging' } : {}),
    ...(typeof s['pickSlot'] === 'number' && Number.isInteger(s['pickSlot']) ? { pickSlot: s['pickSlot'] }
      : typeof s['pickSlot'] === 'string' && /^\d+$/.test(s['pickSlot']) ? { pickSlot: Number(s['pickSlot']) } : {}),
  }),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const taggingRoute = createRoute({ getParentRoute: () => appRoute, path: '/tagging', component: TaggingPage,
  validateSearch: (s: Record<string, unknown>): { asset?: string; snippet?: string; phrase?: string } => (typeof s['asset'] === 'string' ? {
    asset: s['asset'],
    ...(typeof s['snippet'] === 'string' ? { snippet: s['snippet'] } : {}),
    ...(typeof s['phrase'] === 'string' ? { phrase: s['phrase'] } : {}),
  } : {}),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const approveRoute = createRoute({ getParentRoute: () => appRoute, path: '/approve', component: ApprovePage,
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });
const scheduledRoute = createRoute({ getParentRoute: () => appRoute, path: '/scheduled', component: ScheduledPage,
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });
const publishedRoute = createRoute({ getParentRoute: () => appRoute, path: '/published', component: PublishedPage,
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const composeFastRoute = createRoute({ getParentRoute: () => appRoute, path: '/compose-fast', component: ComposeFastPage,
  validateSearch: (s: Record<string, unknown>): { draft?: string } => (typeof s['draft'] === 'string' ? { draft: s['draft'] } : {}),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const composeRoute = createRoute({ getParentRoute: () => appRoute, path: '/compose', component: ComposePage,
  // ?fresh= marks a composition another page just made (the Analyser's Remix): its sounds are new to it.
  validateSearch: (s: Record<string, unknown>): { draft?: string; fresh?: boolean } => ({
    ...(typeof s['draft'] === 'string' ? { draft: s['draft'] } : {}),
    ...(s['fresh'] === true || s['fresh'] === 'true' ? { fresh: true } : {}),
  }),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const archiveRoute = createRoute({ getParentRoute: () => appRoute, path: '/archive', component: ArchivePage,
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const libraryRoute = createRoute({ getParentRoute: () => appRoute, path: '/library', component: LibraryPage,
  // Reached from Tagging's Load more: a clip clicked here goes back to Tagging, not the Analyser.
  // ?media=audio opens the Audio view; ?pack= one import pack in it (from the Upload page's row menu).
  validateSearch: (s: Record<string, unknown>): { from?: 'tagging'; media?: 'audio' | 'photos'; pack?: string } => ({
    ...(s['from'] === 'tagging' ? { from: 'tagging' as const } : {}),
    ...(s['media'] === 'audio' || s['media'] === 'photos' ? { media: s['media'] as 'audio' | 'photos' } : {}),
    ...(typeof s['pack'] === 'string' && /^[0-9a-f-]{36}$/.test(s['pack']) ? { pack: s['pack'] } : {}),
  }),
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const usageRoute = createRoute({ getParentRoute: () => appRoute, path: '/usage', component: UsageLogPage,
  beforeLoad: ({ context }) => {
    const role = context.me?.active?.role;
    if (!role || !roleAtLeast(role, 'editor')) throw redirect({ to: '/' });
  },
  errorComponent: ({ error }) => <ErrorPanel error={error} /> });

const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsPage,
  // Reached from a Tagging cell's gear: the category to bring into view.
  validateSearch: (s: Record<string, unknown>): { category?: string } => (typeof s['category'] === 'string' ? { category: s['category'] } : {}),
  beforeLoad: ({ context }) => {
    if (context.me?.active?.role !== 'owner') throw redirect({ to: '/' });
  } });

function ErrorPanel({ error }: { error: Error }) {
  const access = accessDenied(error);
  return <section className="content col"><div className="card pad error" role="alert">{access ? access.message : error.message}</div></section>;
}

const routeTree = rootRoute.addChildren([signInRoute, signUpRoute, welcomeRoute, callbackRoute, creatorRoute, appRoute.addChildren([homeRoute, uploadRoute, analyseRoute, taggingRoute, browseRoute, composeRoute, composeFastRoute, archiveRoute, libraryRoute, usageRoute, approveRoute, scheduledRoute, publishedRoute, settingsRoute])]);

export const router = createRouter({ routeTree, context: { me: null }, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
